import {
  ThemeProvider,
  createTheme,
  Box,
  Typography,
} from "@mui/material";
import { useParams, useSearchParams } from "react-router-dom";
import { getTeamFromString, Team } from "../../../models/Team";
import { useEffect, useMemo, useState } from "react";
import { usePageTitle } from "../../../hooks/usePageTitle";
import { TeamReportList } from "../../../util/Requests";
import { useCurrentSeason } from "../../../hooks/useCurrentSeason";
import { TeamReportProps } from "../../../TeamReport/types";
import TeamReportComponent from "../../../TeamReport/TeamReport";
import TeamReportSkeleton from "../../../TeamReport/TeamReportSkeleton";
import {
  CustomBuild,
  customBuildText,
  reportSeason,
  reportSeasonType,
  usePlayerBlobs,
  useTeamReport,
} from "../../../TeamReport/useTeamReport";
import { buildReportList } from "./utils";
import ReportFrame from "./ReportFrame";
import {
  useLeagueApi,
  useLeagueEnvironment,
} from "../../../data/LeagueEnvironmentProvider";
import { ExplorerEnvironment } from "../../../data/Cache";
import type { NBAGameListItem } from "../../../util/types";
import type { ExplorerSource } from "../../../models/Explorer";
import { FeatureFlags } from "../../../util/featureFlags";

// Create a minimal theme with no opinionated defaults
const cleanTheme = createTheme();

/**
 * `?source=ss|ctg` asks for an overlap season's comparison report (R1). Dev
 * builds only: a product URL carrying it is ignored, never forwarded.
 */
function devReportSource(params: URLSearchParams): ExplorerSource | null {
  if (!FeatureFlags.sourcePicker) return null;
  const source = params.get("source");
  return source === "ss" || source === "ctg" ? source : null;
}

/** A custom report the cron box is still building: shown above the report being replaced (or the skeleton). */
function CustomBuildNotice({ building }: { building: CustomBuild }) {
  return (
    <Box
      className="trd-webonly"
      role="status"
      sx={{
        px: 2,
        py: 1,
        borderBottom: "1px solid #cfccc8",
        backgroundColor: "#f5f5f5",
        fontSize: "0.8rem",
        color: "#3a3a3a",
      }}
    >
      {customBuildText(building)}
    </Box>
  );
}

export default function TeamReportv2() {
  const { teamAbbrev } = useParams();
  const [searchParams, setSearchParams] = useSearchParams();
  const [team, setTeam] = useState<Team | null>(null);
  const environment = useLeagueEnvironment();
  const api = useLeagueApi();
  // The backend's season (first regular-season game): null until it answers
  const seasons = useCurrentSeason(api);
  const currentSeason = seasons?.season ?? null;
  // The coming season once its preseason starts: its games are listed too, before it becomes current
  const seasonAny = seasons?.seasonAny ?? null;

  // Two independent data sources for building the report list
  const [games, setGames] = useState<NBAGameListItem[] | null>(null);
  const [dbReportList, setDbReportList] = useState<TeamReportList | null>(null);

  const leagueLabel =
    environment === ExplorerEnvironment.NBA ? "NBA" : "G League";

  usePageTitle(
    teamAbbrev
      ? `${leagueLabel} Team Reports - ${teamAbbrev.toUpperCase()} - Explorer`
      : `${leagueLabel} Team Reports - Explorer`,
  );

  useEffect(() => {
    setTeam(getTeamFromString(teamAbbrev ?? "", environment));
  }, [teamAbbrev, environment]);

  // Derive merged report list from both data sources.
  // Recomputes whenever either source updates — no race conditions.
  const reportList = useMemo<TeamReportList | null>(() => {
    if (!team || currentSeason === null) return null;

    // Schedule-based list: seasons from year range, games + playoff detection from NBA API
    const list = buildReportList(games || [], team, currentSeason);

    // Merge DB-sourced reports (playoff_season, L10, L20)
    if (dbReportList) {
      if (dbReportList.playoff_season?.length) {
        const dbPlayoffs = dbReportList.playoff_season.map((r) => ({
          ...r,
          id: `${team.abbrev}-playoff_season-${r.season}`,
        }));
        // DB entries take precedence, then any schedule-only entries for other seasons
        const dbSeasons = new Set(dbPlayoffs.map((r) => r.season));
        const scheduleOnly = list.playoff_season.filter(
          (r) => !dbSeasons.has(r.season),
        );
        list.playoff_season = [...dbPlayoffs, ...scheduleOnly];
      }
      // Window reports, under the app's report ids (POR-L10-2025) like the playoff list above
      (["L5", "L10", "L20"] as const).forEach((type) => {
        const rows = dbReportList[type];
        if (rows?.length) {
          list[type] = rows.map((r) => ({ ...r, id: `${team.abbrev}-${type}-${r.season}` }));
        }
      });
      if (dbReportList.preseason_season?.length) {
        list.preseason_season = dbReportList.preseason_season.map((r) => ({
          ...r,
          id: `${team.abbrev}-preseason_season-${r.season}`,
        }));
      }
      // A stored season report ahead of the schedule's current season is the roster
      // preview placeholder; the default report stays the current season.
      const ahead = (dbReportList.season ?? [])
        .filter((r) => r.season > currentSeason)
        .map((r) => ({
          id: `${team.abbrev}-season-${r.season}`,
          season: r.season,
          preview: true,
        }));
      list.season = [...list.season, ...ahead];
    }

    return list;
  }, [team, games, dbReportList, currentSeason]);

  // The season of the report being viewed: a season report's from its id, a game's from its NBA game id
  // (0022500770: 2025-26). Its schedule is fetched too, so the picker can show a game from an earlier season.
  const reportParamValue = searchParams.get("report");
  const viewedSeason = useMemo(() => {
    if (!reportParamValue) return null;
    const game = reportParamValue.split("-game-")[1];
    const n = game ? 2000 + parseInt(game.slice(3, 5), 10) : parseInt(reportParamValue.split("-").pop() ?? "", 10);
    return Number.isFinite(n) ? n : null;
  }, [reportParamValue]);

  // Fetch both data sources independently
  useEffect(() => {
    if (!team || currentSeason === null) return;

    // Fast: DB report list (playoff_season, L10, L20)
    api
      .getTeamReportList(team)
      .then(({ data }) => setDbReportList(data))
      .catch((err) => console.error("Error fetching report list:", err));

    // Slow: NBA games API (game schedule), plus the coming season's during its preseason
    const gameSeasons = Array.from(
      new Set([
        currentSeason,
        ...(seasonAny !== null && seasonAny > currentSeason ? [seasonAny] : []),
        ...(viewedSeason !== null && viewedSeason <= Math.max(currentSeason, seasonAny ?? currentSeason)
          ? [viewedSeason]
          : []),
      ]),
    );
    // The schedule's games carry no season of their own: stamp the one they were fetched for
    Promise.all(gameSeasons.map((s) => api.getTeamGames(team.nbaId, s)))
      .then((lists) =>
        setGames(
          lists.flatMap((data, i) =>
            (data || []).map((g) => ({ ...g, season: g.season ?? gameSeasons[i] })),
          ),
        ),
      )
      .catch((err) => console.error("Error fetching games:", err));
  }, [team, api, currentSeason, seasonAny, viewedSeason]);

  // Only the default report needs the current season; a URL names its own, and
  // must not refetch when the season arrives.
  const defaultReportSeason = searchParams.get("report") ? null : currentSeason;
  const reportParam = searchParams.get("report");
  const requestedId =
    reportParam ||
    (team && defaultReportSeason !== null ? `${team.abbrev}-season-${defaultReportSeason}` : null);
  const devSource = useMemo(() => devReportSource(searchParams), [searchParams]);
  const {
    report,
    reportId: selectedReportId,
    loading,
    error,
    building,
  } = useTeamReport(api, team, requestedId, devSource);
  const playerBlobs = usePlayerBlobs(api, report, selectedReportId);

  // A roster preview leads with the stats season's full report (Trevor, 2026-10-19): its team pages, then the coming
  // roster's player pages on that season's numbers. Without one the preview is player pages only.
  const [previewTeamReport, setPreviewTeamReport] = useState<TeamReportProps | null>(null);
  useEffect(() => {
    setPreviewTeamReport(null);
    const statsSeason = report?.previewStatsSeason;
    if (!team || statsSeason == null) return;
    let cancelled = false;
    api
      .getTeamSeasonReport(team, statsSeason, "season")
      .then((statsReport) => {
        if (!cancelled) setPreviewTeamReport(statsReport);
      })
      .catch((err) => console.error("Error fetching the preview's season report:", err));
    return () => {
      cancelled = true;
    };
  }, [report, team, api]);

  const showOwnershipPage = searchParams.get("postgame") === "ownership";

  // Handle report selection change
  const handleReportChange = (reportId: string) => {
    if (!team) return;
    const newParams = new URLSearchParams();
    newParams.set("report", reportId);
    const devSource = devReportSource(searchParams);
    if (devSource) newParams.set("source", devSource);
    // Switching games keeps the ownership view (a season pick drops it —
    // the ownership page is game-only).
    if (showOwnershipPage && reportId.includes("-game-")) {
      newParams.set("postgame", "ownership");
    }
    setSearchParams(newParams);
  };

  return (
    <ThemeProvider theme={cleanTheme}>
      <ReportFrame fullPrintWidth={showOwnershipPage}>
        {error ? (
          <Box
            // Headless printing (postgame packet Phase 3) waits on this flag;
            // "error" tells it to stop waiting.
            data-report-ready="error"
            sx={{
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              Height: 400,
              p: 4,
            }}
          >
            <Typography>
              {building?.status === "failed" ? customBuildText(building) : "Unable to load report."}
            </Typography>
          </Box>
        ) : !report ? (
          <>
            {building && <CustomBuildNotice building={building} />}
            <TeamReportSkeleton isOwnershipReport={showOwnershipPage} />
          </>
        ) : (
          <>
          {building && <CustomBuildNotice building={building} />}
          <TeamReportComponent
            {...report}
            playerBlobs={playerBlobs ?? undefined}
            previewTeamReport={previewTeamReport}
            reportList={reportList || undefined}
            selectedReportId={selectedReportId}
            onReportChange={handleReportChange}
            loading={loading}
            showOwnershipPage={showOwnershipPage}
            teamId={team?.nbaId?.toString()}
            seasonType={reportSeasonType(requestedId, report)}
            season={reportSeason(requestedId)}
          />
          </>
        )}
      </ReportFrame>
    </ThemeProvider>
  );
}
